import React, { Component } from 'react';
import { Form, Alert, Table, Button, Badge, Modal, Row, Col } from 'react-bootstrap';
import { GoRepo, GoLock, GoTrash, GoPerson } from "react-icons/go";
import Select from 'components/controls/select';
import ConfirmModal from 'components/controls/confirmModal';
import UserIdentity from 'components/controls/userIdentity';
import Utils from 'context/utils';

class AddMemberModal extends Component {
  constructor(props) {
    super(props);

    this.roleOptions = [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
      { value: 'admin', label: 'Admin' },
    ];

    this.state = {
      member: {
        alias: "",
        role: this.roleOptions[0]
      },
      accountOptions: [],
      selectedAccount: null
    };

    this.form = React.createRef();
  }

  onEnter = () => {
    this.setState({
      member: { alias: "", role: this.roleOptions[0] },
      accountOptions: [],
      selectedAccount: null
    });

    // Only for an organization: listing an owner's accounts requires ADMIN over that owner, which a
    // blob administrator need not hold. The plain field still reaches an account, because
    // POST /v1/members resolves user_id as an alias OR a raw id.
    if ( "org" !== this.props.target ) {
      return;
    }
    this.props.app.api.usersTargetTargetIdGet("org", this.props.targetId)
      .then(response => {
        this.setState({
          accountOptions: (response.data.users || []).map(account => ({
            value: account.id,
            label: account.name
          }))
        });
      }, _ => {
        // An owner with no accounts, or a listing we may not read: the plain field still works.
      });
  }

  handleAddMember = () => {
    // Check form validity
    if ( !this.form.current.reportValidity() ) {
      return;
    }

    // Initiate processing
    const member = {
      user_id: this.state.selectedAccount ? this.state.selectedAccount.value : this.state.member.alias,
      role: this.state.member.role.value
    };
    this.props.handleAddMember(member);

    // Close modal
    this.props.onHide();
  }

  // Only what a Modal takes is passed to it. This component's own props — app, target, targetId,
  // handleAddMember — are not Modal props, and spreading them all in lands them on a DOM node and
  // warns about unknown attributes.
  render() {
    return (
      <Modal
        show={this.props.show}
        onHide={this.props.onHide}
        onEnter={this.onEnter}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Add New Member
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={this.form}>
            <Form.Group>
              <Form.Label>
                <strong>User Alias</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={3}>
                  <Form.Control
                    required={!this.state.selectedAccount}
                    disabled={!!this.state.selectedAccount}
                    type="text"
                    size="sm"
                    autoFocus
                    value={this.state.member.alias}
                    onChange={(event) => {
                      let member = this.state.member;
                      member.alias = event.target.value;
                      this.setState({member: member});
                    }}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                Enter a unique user alias (this can be found on the user's profile page).
              </Form.Text>
            </Form.Group>

            {0 !== this.state.accountOptions.length &&
              <Form.Group>
                <Form.Label>
                  <strong>Or a Service Account</strong>
                </Form.Label>
                <Row>
                  <Col sm={4}>
                    <Select
                      isClearable
                      options={this.state.accountOptions}
                      value={this.state.selectedAccount}
                      onChange={(option) => {
                        this.setState({
                          selectedAccount: option,
                          member: { ...this.state.member, alias: "" }
                        });
                      }}
                    />
                  </Col>
                </Row>
                <Form.Text muted>
                  An account has no alias, so it is chosen here rather than typed. Membership is what
                  it can reach: removing it revokes access without touching its keys.
                </Form.Text>
              </Form.Group>
            }
            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Role</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={4}>
                  <Select
                    options={this.roleOptions}
                    value={this.state.member.role}
                    onChange={(option) => {
                      let member = this.state.member;
                      member.role = option;
                      this.setState({member: member});
                    }}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                Selecting the right role is critical to make sure that target data is properly protected.
              </Form.Text>
              <Form.Text muted>
                The following roles are supported:
              </Form.Text>
              <Form.Text muted>
                <ul>
                  <li><strong>Read</strong> Provides read-only access to the target, its properties, and child objects.</li>
                  <li><strong>Write</strong> Allows modifying the target, its properties, and child objects.</li>
                  <li><strong>Admin</strong> Empowers users to administer the the target, and its child objects (full control).</li>
                </ul>
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" onClick={this.handleAddMember}>Add</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

class Member extends Component {
  constructor(props) {
    super(props);

    this.state = {
      deleting: false,
      deletingError: null,
      memberUser: null,
      confirmModalShow: false
    };
  }

  componentWillMount() {
    this.setState({
      memberUser: null
    });
  }

  componentDidMount() {
    this.loadMemberUser();
  }

  componentDidUpdate(prevProps) {
    if (this.props.member["id"] !== prevProps.member["id"]) {
      this.loadMemberUser();
    }
  }

  loadMemberUser() {
    this.props.app.api.usersIdGet(this.props.member["user_id"])
      .then(response => {
        this.setState({
          memberUser: response.data.user
        });
      }, _ => {});
  }

  // Identifies the member being deleted for the confirmation body: the resolved user's display name
  // once loadMemberUser() resolves, falling back to the raw member.user_id before/if that lookup
  // fails. An account has a name and no alias, so `alias` alone was never enough.
  memberDisplayName() {
    if ( this.state.memberUser ) {
      return Utils.displayName(this.state.memberUser, this.props.member.user_id);
    }
    return this.props.member.user_id;
  }

  handleDelete() {
    this.setState({
      deleting: true,
      deletingError: null
    });
    this.props.accessInterface.setModifying(true);

    const memberId = this.props.member.id;
    this.props.app.api.membersIdDelete(memberId)
      .then(_ => {
        this.setState({deleting: false});
        this.props.accessInterface.setModifying(false);

        this.props.accessInterface.loadMembers();
      }, error => {
        this.setState({
          deleting: false,
          deletingError: error.message
        });
        this.props.accessInterface.setModifying(false);
      });
  }

  render() {
    return (
      <>
        <tr>
          <td>
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-baseline">
                {this.state.memberUser
                  ? <UserIdentity user={this.state.memberUser} fallback={this.props.member.user_id}/>
                  : <><GoPerson/>&nbsp;<span className="text-muted text-note">({this.props.member.user_id})</span></>
                }
                &nbsp;&nbsp;
                <Badge variant="secondary" className="title-tag-badge">{this.props.member.role}</Badge>
              </div>

              <div className="ml-auto">
                {!this.props.accessInterface.modifying &&
                  <Button
                    variant="danger"
                    className="btn-icon"
                    onClick={() => this.setState({confirmModalShow: true})}
                    ><GoTrash /></Button>
                }
              </div>
            </div>
          </td>
        </tr>
        {this.state.deleting &&
          <tr>
            <td style={{padding: "0px"}}>
              <div>
                <Alert variant="primary">
                  Deleting member...
                </Alert>
              </div>
            </td>
          </tr>
        }
        {this.state.deletingError &&
          <tr>
            <td style={{padding: "0px"}}>
              <div>
                <Alert variant="warning">
                  {this.state.deletingError}
                </Alert>
              </div>
            </td>
          </tr>
        }

        <ConfirmModal
          show={this.state.confirmModalShow}
          onHide={() => this.setState({confirmModalShow: false})}
          onConfirm={() => this.handleDelete()}
          title="Delete Member"
          body={
            <>
              Are you sure you want to remove <strong>{this.memberDisplayName()}</strong> from this target?
              They will immediately lose access.
            </>
          }
        />
      </>
    );
  }
}

export default class Access extends Component {
  constructor(props) {
    super(props);

    this.state = {
      modifying: false,
      updatingVisibility: false,
      updatingVisibilityError: null,
      creatingMember: false,
      creatingMemberError: null,
      members: null,
      setModifying: this.setModifying,
      loadMembers: this.loadMembers,
      addMemberModalShow: false,
      setAddMemberModalShow: (show) => {
        this.setState({addMemberModalShow: show});
      }
    };
  }

  componentDidMount() {
    this.loadMembers();
  }

  setModifying = (modifying) => {
    this.setState({modifying: modifying});
  }

  loadMembers = () => {
    this.props.app.api.membersTargetTargetIdGet(this.props.target, this.props.targetObject.id)
      .then(response => {
        this.setState({
          members: response.data.members
        });
      }, _ => {});
  }

  handleUpdateVisibility = (visibility) => {
    this.setState({
      modifying: true,
      updatingVisibility: true,
      updatingVisibilityError: null
    });

    let promise = null;
    const patch = {
      "visibility": visibility
    };
    if ( "org" === this.props.target ) {
      const orgId = this.props.targetObject["id"];
      promise = this.props.app.api.orgsIdPatch(orgId, patch);
    } else if ( "blob" === this.props.target ) {
      const orgId = this.props.targetObject["org_id"];
      const blobId = this.props.targetObject["id"];
      promise = this.props.app.api.blobsIdPatch(orgId, blobId, patch)
    } else {
      return;
    }

    promise.then(_ => {
      this.setState({
        modifying: false,
        updatingVisibility: false,
        updatingVisibilityError: null
      });

      // Refresh the view
      this.props.profileInterface.loadProfile();
    }, error => {
      // Indicate error
      this.setState({
        modifying: false,
        updatingVisibility: false,
        updatingVisibilityError: error.message
      });
    });
  }

  handleAddMemberInit() {
    this.setState({
      modifying: false,
      creatingMember: false,
      creatingMemberError: null,
      addMemberModalShow: true
    });
  }

  handleAddMember = (member) => {
    this.setState({
      modifying: true,
      creatingMember: true,
      creatingMemberError: null
    });

    // Create member
    this.props.app.api.membersTargetTargetIdPost(this.props.target, this.props.targetObject.id, member)
      .then(_ => {
        this.setState({
          modifying: false,
          creatingMember: false,
          creatingMemberError: null
        });

        // Refresh the list of members
        this.loadMembers();
      }, error => {
        // Indicate error
        this.setState({
          modifying: false,
          creatingMember: false,
          creatingMemberError: error.message
        });
      });
  }

  render() {
    return (
      <>
        <h5>Visibility</h5>
        <hr/>
        <Form>
          <Form.Group>
            <Form.Check
              type="radio"
              name="visibility"
              id="visibility-public"
              label={<><GoRepo/> Public</>}
              disabled={this.state.updating}
              checked={"public" === this.props.targetObject.visibility}
              onChange={(event) => {
                const visibility = event.target.checked ? "public" : "private";
                this.handleUpdateVisibility(visibility);
              }}
            />
            <Form.Text muted>
              <string>Public</string> targets are visible to the world but only chosen users can contribute to it.
            </Form.Text>
            <Form.Check
              type="radio"
              name="visibility"
              id="visibility-private"
              label={<><GoLock/> Private</>}
              disabled={this.state.updating}
              checked={"private" === this.props.targetObject.visibility}
              onChange={(event) => {
                const visibility = event.target.checked ? "private" : "public";
                this.handleUpdateVisibility(visibility);
              }}
            />
            <Form.Text muted>
              <string>Private</string> targets are invisible by default with both producers and consumers being added manually.
            </Form.Text>

            {this.state.updatingVisibility &&
              <div className="content-row">
                <Alert variant="primary">
                  Changing visibility...
                </Alert>
              </div>
            }
            {this.state.updatingVisibilityError &&
              <div className="content-row">
                <Alert variant="warning">
                  {this.state.updatingVisibilityError}
                </Alert>
              </div>
            }
          </Form.Group>
        </Form>

        <h5>Members</h5>
        <hr/>

        <div>
          Membership enables authenticated BlobHub users with access to target objects.
        </div>

        {this.state.members && 0 !== this.state.members.length &&
          <div className="content-row">
            <Table hover bordered style={{"margin-bottom": "0px"}}>
              <tbody>
                {this.state.members.map((member, _) => {
                  return (
                    <Member
                      app={this.props.app}
                      accessInterface={this.state}
                      member={member}
                      key={member.id}
                    />
                  );
                })}
              </tbody>
            </Table>
          </div>
        }

        {!this.state.modifying &&
          <div className="content-row">
            <Button
              variant="success"
              size="sm"
              onClick={() => this.handleAddMemberInit()}><GoPerson/> New
            </Button>

            <span className="text-muted text-note">
              &nbsp;&nbsp;&nbsp;Give someone access to view or modify this target
            </span>&nbsp;&nbsp;&nbsp;
          </div>
        }

        {this.state.creatingMember &&
          <div className="content-row">
            <Alert variant="primary">
              Creating member...
            </Alert>
          </div>
        }
        {this.state.creatingMemberError &&
          <div className="content-row">
            <Alert variant="warning">
              {this.state.creatingMemberError}
            </Alert>
          </div>
        }

        <AddMemberModal
          show={this.state.addMemberModalShow}
          onHide={() => this.state.setAddMemberModalShow(false)}
          handleAddMember={this.handleAddMember}
          app={this.props.app}
          target={this.props.target}
          targetId={this.props.targetObject.id}
        />
      </>
    );
  }
}
